import React, {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {
  Button,
  ChevronDownIcon,
  ExpandBoxIcon,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import Draggable, {DraggableData, DraggableEvent} from 'react-draggable';
import DragToRotate, {DragToRotateStatus} from './DragToRotate';
import {ThreeDPreviewStatus} from '../../Enums';
import {useSelector} from 'react-redux';
import {LayoutSelector} from '../../Selectors';
import {useTaopix} from '../Context/TaopixContext';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';

interface ThreeDPreviewProps {
  status: ThreeDPreviewStatus;
  isSmallScreen: boolean;
  fullScreen: boolean;
  floating?: boolean;
  showEditButton?: boolean;
  showToggle?: boolean;
  classes?: string;
}

const ThreeDPreview = ({
  status,
  isSmallScreen,
  fullScreen,
  floating = true,
  showEditButton = true,
  showToggle = true,
  classes,
}: ThreeDPreviewProps) => {
  const taopix = useTaopix();
  const productLayout = useSelector(LayoutSelector);
  const {t} = useTranslation();
  const [isFullScreen, setIsFullScreen] = React.useState<boolean>(fullScreen);
  const [dragTipStatus, setDragTipStatus] = React.useState<DragToRotateStatus>(
    DragToRotateStatus.Pending
  );
  const draggableRef: React.MutableRefObject<Draggable> = React.useRef<Draggable>({} as Draggable);
  const timeout = React.useRef<number>(0);
  const coords = React.useRef<{x: number; y: number}>({x: 0, y: 0});
  const wrapperContainerRef = React.useRef<HTMLDivElement>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const transformValue = React.useRef<string>('');
  const layoutCode = React.useRef<string>('');
  const containerId: Readonly<string> = 'threedpreview';
  const dragToleranceValue: Readonly<number> = 2.5;
  const ui = useUiState();
  const [modelStatus, setModelStatus] = React.useState<ThreeDPreviewStatus>(
    ThreeDPreviewStatus.None
  );

  React.useEffect(() => {
    setModelStatus(status);
  }, [status]);

  React.useEffect(() => {
    if (ThreeDPreviewStatus.Loaded === modelStatus) {
      // Model has loaded so we can start the process.

      // Store the layout code, so we can track product changes.
      layoutCode.current = productLayout.code;

      if (DragToRotateStatus.Dismissed !== dragTipStatus) {
        setDragTipStatus(DragToRotateStatus.Pending);
      }
      window.Taopix.Core.get('threeDPreviewManager').initialise(containerId);
    }
  }, [isSmallScreen, modelStatus]);

  React.useEffect(() => {
    toggleOverlay();
    transformValue.current = '';
  }, [isSmallScreen]);

  React.useEffect(() => {
    if (isSmallScreen) {
      if (!isFullScreen && wrapperContainerRef.current && '' !== transformValue.current) {
        wrapperContainerRef.current.style.transform = transformValue.current;
      } else if (isFullScreen && wrapperContainerRef.current) {
        wrapperContainerRef.current.style.removeProperty('transform');
      }

      toggleOverlay();
    }
    if (isFullScreen) {
      taopix.core.dispatch('SHAPE.DESELECT', null).catch(error => {
        console.error(error);
      });
    }
  }, [isFullScreen]);

  /**
   * Toggle the overlay to block being able to rotate the model.
   */
  const toggleOverlay = (): void => {
    if (overlayRef.current) {
      if (!isFullScreen && isSmallScreen) {
        // If closed, move the overlay above to block rotating the model.
        overlayRef.current.style.zIndex = '30';
      } else {
        overlayRef.current.style.removeProperty('z-index');
      }
    }
  };

  /**
   * Handle when the drag extention has stopped.
   * If the preview has not moved then treat it as a click.
   *
   * @param event Draggable event.
   * @param draggableData Preview moved coordinates.
   */
  const handleStopDrag = (event: DraggableEvent, draggableData: DraggableData): void | false => {
    const {lastX, lastY} = draggableData;

    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    // Add a set timeout to make sure the drag is complete and not started again.
    timeout.current = window.setTimeout(() => {
      // Check if the object has moved, respecting a tolerance.
      // If the object has not moved, trigger a click.
      if (
        coords.current.x > lastX - dragToleranceValue &&
        coords.current.x < lastX + dragToleranceValue &&
        coords.current.y > lastY - dragToleranceValue &&
        coords.current.y < lastY + dragToleranceValue
      ) {
        // Store and then remove the transform value so the full screen position is correct.
        if (wrapperContainerRef.current) {
          transformValue.current = wrapperContainerRef.current.style.transform;
        }

        // Call the click action.
        setIsFullScreen(!isFullScreen);
        return false;
      } else {
        // Store the current position.
        coords.current.x = lastX;
        coords.current.y = lastY;
      }
    }, 10);
  };

  /**
   * Wrapper for adding the Draggable component.
   *
   * @param children The element to apply the Draggable component to.
   * @returns The wrapped children component.
   */
  const addDraggable = (children: ReactNode): ReactNode => {
    return isSmallScreen ? (
      <Draggable disabled={isFullScreen} bounds={'main'} onStop={handleStopDrag} ref={draggableRef}>
        {children}
      </Draggable>
    ) : (
      children
    );
  };

  const containerClassNames = classNames('w-full', isFullScreen ? 'flex-1' : 'h-full');

  // To do: Separate the floating container from actual 3D preview so we can use it 'inline' i.e. in the assistant content
  const resizeContainerClassNames = classNames(
    'flex flex-1 flex-col py-lg gap-sm',
    isFullScreen
      ? [
          'lg:w-full',
          'lg:h-full',
          floating && [
            'z-50',
            'sm:fixed',
            ui.state.focusMode ? 'sm:top-sm' : 'sm:top-[70rem]',
            'sm:left-sm',
            'sm:bottom-sm',
            'sm:right-sm',
            'lg:fixed',
            'lg:bottom-0',
            'lg:left-0',
            'sm:shadow-[0_0_3px_1px_rgba(0,0,0,0.3)]',
          ],
        ]
      : [
          'relative',
          'lg:w-[320rem]',
          'lg:max-h-[200rem]',
          'sm:bottom-[70rem]',
          'sm:left-sm',
          'sm:fixed',
          'sm:w-[100rem]',
          'sm:h-[100rem]',
        ]
  );
  const fullscreenToggleButtonClassNames = classNames('absolute top-xs right-xs z-10');

  return (
    <>
      {addDraggable(
        <Theme
          name={ThemeName.Popouts}
          allowCorners={false}
          className={classNames(resizeContainerClassNames, classes)}
          ref={wrapperContainerRef}
        >
          {/* The overlay div is for smallscreen to block being able to rotate the model while in the draggable floating preview mode */}
          <div
            id={'threedpreview-overlay'}
            className={'h-full w-full z-[-1] absolute top-0 left-0 lg:hidden'}
            ref={overlayRef}
          />
          {showToggle && (
            <Button
              id={'threedpriew-toggle-fullscreen'}
              onClick={() => setIsFullScreen(!isFullScreen)}
              label={''}
              size={'small'}
              buttonStyle={isFullScreen ? 'tertiary' : 'primary'}
              hideLabel
              className={fullscreenToggleButtonClassNames}
              startIcon={isFullScreen ? <ChevronDownIcon /> : <ExpandBoxIcon />}
            />
          )}
          {!(isSmallScreen && !isFullScreen) && (
            <DragToRotate
              isFullScreen={isFullScreen}
              status={dragTipStatus}
              threeDContainerId={containerId}
              onStatusChange={(newStatus: DragToRotateStatus) => setDragTipStatus(newStatus)}
            />
          )}
          <div id={containerId} className={containerClassNames}>
            <p className="text-sm text-center flex h-full justify-center items-center">
              {t('str_LabelGenerating3DModel')}
            </p>
          </div>
          {isFullScreen && (
            <div>
              {showEditButton && (
                <Button
                  type={'button'}
                  id={'threedpreview-edit'}
                  buttonStyle={'primary'}
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  label={t('str_ButtonEdit')}
                  className={'m-auto'}
                />
              )}
              <p className={'text-center mt-sm text-sm px-sm'}>{t('str_Label3DModelDisclaimer')}</p>
            </div>
          )}
        </Theme>
      )}
    </>
  );
};

export default ThreeDPreview;
